import { SIDE_TABLE_COLUMN_TYPES, tableSideColumnMapper } from "@loanbossinc/component-library";
import { defaultField, defaultPercentField } from "helpers/row-logic-helper";

import { shouldShowField } from "../loanDetailEditUtil";

import {
  shouldShowProceeds,
  shouldShowProceedsRemaining,
  shouldShowLastLoanSpread,
  shouldShowMezzFundingStructureId,
  shouldShowMezzRateTypeId,
  shouldShowPaidOffDate,
  shouldShowActualPrepaymentPenalty,
  shouldShowBalanceAtPayoff,
  shouldShowFallbackRate,
  getProceedsDrawnAsOfDate,
  getClosingCostDateIsAssumed,
  getLastModifiedDate,
  shouldShowRate,
  shouldShowLoanAmount,
  shouldShowCurrentPrincipalBalance,
  shouldShowNextPayment,
  shouldShowClosingDate,
  shouldShowMaturityDate,
  shouldShowExtendedMaturityDate,
  shouldShowIndex,
  shouldShowAllInRate,
  shouldShowRateTypeId,
  shouldShowDaycount,
  isLoanFloatingRateType,
  shouldShowConversionOption
} from "./section-general-information-row-logic";

const DEFAULT_WIDTH = 138;

// `flags` is intended to be an object obtained from the useFlags hook.
export const rows = (flags) => {
  
  const hideLoanEconomicsFields = flags.bad139HideLoanEconomicsFields;
  
  return {
    loanAmount: {
      isVisible: loan => shouldShowLoanAmount(loan),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Loan Amount",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
          map: data => defaultField(data, "details.amount"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    currentPrincipalBalance: {
      isVisible: loan => shouldShowCurrentPrincipalBalance(loan),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Current Loan Balance",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
          map: data => defaultField(data, "details.currentPrincipalBalance"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    nextPayment: {
      isVisible: loan => shouldShowNextPayment(loan),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Next Payment",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
          map: data => defaultField(data, "details.currentPayment"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    initialProceeds: {
      isVisible: data => shouldShowProceeds(data) && !hideLoanEconomicsFields,
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Initial Proceeds",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
          map: data => defaultField(data, "details.initialProceeds"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    proceedsDrawn: {
      isVisible: data => shouldShowField(data, "details.proceedsDrawn") && shouldShowProceeds(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Proceeds Drawn",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
          map: data => defaultField(data, "details.proceedsDrawn"),
          Cell: tableSideColumnMapper,
          subtextMappers: [
            { map: data => getProceedsDrawnAsOfDate(data) }
          ]
        }
      ]
    },
    proceedsRemaining: {
      isVisible: data => shouldShowProceedsRemaining(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Proceeds Remaining",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
          map: data => defaultField(data, "details.loanProceedsAvailable"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    closingDate: {
      isVisible: loan => shouldShowClosingDate(loan),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Closing Date",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DATE_SUBTEXT,
          map: data => defaultField(data, "details.closingDate"),
          Cell: tableSideColumnMapper,
          subtextMappers: [
            { map: data => getClosingCostDateIsAssumed(data) },
            { map: data => getLastModifiedDate(data) }
          ]
        }
      ]
    },
    maturityDate: {
      isVisible: data => shouldShowMaturityDate(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Maturity Date",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DATE,
          map: data => defaultField(data, "details.maturityDate"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    extendedMaturityDate: {
      isVisible: data => shouldShowExtendedMaturityDate(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Extended Maturity Date",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DATE,
          map: data => defaultField(data, "details.extendedMaturityDate"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    allInRate: {
      isVisible: data => shouldShowAllInRate(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "All-In Rate",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
          map: data => defaultPercentField(data, "details.allInRate", 100),
          Cell: tableSideColumnMapper,
          decimals: 2
        }
      ]
    },
    loanSpread: {
      isVisible: data => shouldShowLastLoanSpread(data) && !hideLoanEconomicsFields,
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Loan Spread",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
          decimals: 3,
          map: data => defaultPercentField(data, "details.loanSpread", 100),
          Cell: tableSideColumnMapper
        }
      ]
    },
    index: {
      isVisible: data => shouldShowIndex(data) && !hideLoanEconomicsFields,
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Index",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.STRING,
          map: data => defaultField(data, "details.index.name"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    rateTypeId: {
      isVisible: data => shouldShowRate(data) && !hideLoanEconomicsFields,
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Rate Type",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.STRING,
          map: data => defaultField(data, "details.rateTypeRef.name"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    interestRate: {
      isVisible: data => shouldShowRate(data) && !hideLoanEconomicsFields,
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Rate",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
          map: data => defaultPercentField(data, "details.interestRate", 100),
          decimals: 2,
          Cell: tableSideColumnMapper
        }
      ]
    },
    daycount: {
      isVisible: data => shouldShowDaycount(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Daycount",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.STRING,
          map: data => defaultField(data, "details.daycount.name"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    lookback: {
      isVisible: loan => shouldShowField(loan, "details.lookbackDays") && isLoanFloatingRateType(loan),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Lookback",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DECIMAL,
          map: data => defaultField(data, "details.lookbackDays"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    rounding: {
      isVisible: loan => shouldShowField(loan, "details.roundingId") && isLoanFloatingRateType(loan),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Rounding",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.STRING,
          map: data => defaultField(data, "details.rounding.name"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    embeddedFloor: {
      isVisible: loan => shouldShowField(loan, "details.indexFloor") && isLoanFloatingRateType(loan),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Embedded Floor",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
          map: data => defaultPercentField(data, "details.indexFloor", 100),
          Cell: tableSideColumnMapper,
          decimals: 3
        }
      ]
    },
    embeddedCap: {
      isVisible: loan => shouldShowField(loan, "details.embeddedCap") && isLoanFloatingRateType(loan),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Embedded Cap",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
          map: data => defaultPercentField(data, "details.embeddedCap", 100),
          Cell: tableSideColumnMapper,
          decimals: 3
        }
      ]
    },
    rateModification: {
      isVisible: loan => shouldShowField(loan, "details.spreadReductionTerms") && isLoanFloatingRateType(loan),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Rate Modification",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.TEXT,
          map: data => defaultField(data, "details.spreadReductionTerms"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    conversionOption: {
      isVisible: data => shouldShowConversionOption(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Conversion Options",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.TEXT,
          map: data => defaultField(data, "details.conversionOption"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    mezzFundingStructureId: {
      isVisible: data => shouldShowMezzFundingStructureId(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Funding Structure",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.STRING,
          map: data => defaultField(data, "details.mezzFundingStructure.name"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    mezzRateTypeId: {
      isVisible: data => shouldShowMezzRateTypeId(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Mezz Rate",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.STRING,
          map: data => defaultField(data, "details.mezzRateTypeRef.name"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    paidOffDate: {
      isVisible: data => shouldShowPaidOffDate(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Paid Off Date",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DATE,
          map: data => defaultField(data, "details.paidOffDate"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    actualPrepaymentPenalty: {
      isVisible: data => shouldShowActualPrepaymentPenalty(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Actual Prepay Penalty",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
          map: data => defaultField(data, "details.actualPrepaymentPenalty"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    balanceAtPayoff: {
      isVisible: data => shouldShowBalanceAtPayoff(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Balance at Payoff",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
          map: data => defaultField(data, "details.balanceAtPayoff"),
          Cell: tableSideColumnMapper
        }
      ]
    },
    fallbackRate: {
      isVisible: data => shouldShowFallbackRate(data),
      cells: [
        {
          type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
          width: DEFAULT_WIDTH,
          label: "Fallback Rate",
          Cell: tableSideColumnMapper
        },
        {
          type: SIDE_TABLE_COLUMN_TYPES.STRING,
          map: data => defaultField(data, "details.fallbackRate"),
          Cell: tableSideColumnMapper
        }
      ]
    }
  }
};
